"use client"

import { useEffect, useState, useRef } from "react"
import "./Header.css"
import { useNavigate, Link, useLocation } from "react-router-dom"
import Cookies from "js-cookie"
import axios from "axios"
import Logo from "../img/logo3.png"

const NAV_PRIMARY = [
  { path: "/profile",   label: "Dashboard",  icon: "⊞" },
  { path: "/customers", label: "Customers",  icon: "👥" },
  { path: "/bookings",  label: "Bookings",   icon: "📋" },
  { path: "/booking",   label: "Payments",   icon: "💳" },
  { path: "/equipment", label: "Equipment",  icon: "🏋️" },
  { path: "/slots",     label: "Slots",      icon: "⏰" },
]

const NAV_MORE = [
  { path: "/gallery",      label: "Gallery",      icon: "🖼️" },
  { path: "/banking",      label: "Banking",      icon: "🏦" },
  { path: "/subscription", label: "Subscription", icon: "👑" },
]

const ALL_NAV = [...NAV_PRIMARY, ...NAV_MORE]

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const navigate = useNavigate()
  const loginId = Cookies.get("auth")
  const location = useLocation()
  const moreRef = useRef(null)

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/verify-token`,
          {},
          { headers: { Authorization: loginId } }
        )
        if (response.data.message !== "Token is valid") {
          Cookies.remove("auth")
          navigate("/login")
        }
      } catch {
        Cookies.remove("auth")
        navigate("/login")
      }
    }
    if (loginId) verifyToken()
  }, [loginId, navigate])

  const handleLogout = () => {
    Cookies.remove("auth")
    navigate("/register")
  }

  const isActive = (path) => location.pathname === path
  const isMoreActive = NAV_MORE.some(l => location.pathname === l.path)

  return (
    <>
      <header className={`header ${isScrolled ? "scrolled" : ""}`}>
        <div className="container">
          {/* Logo */}
          <div className="logo-container">
            <Link className="logo-link" to={loginId ? "/profile" : "/"}>
              <div className="logo-icon">
                <img src={Logo} alt="Yupluck" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
              </div>
              <span className="logo-text">Yupluck</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {loginId ? (
              <>
                {NAV_PRIMARY.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`nav-link ${isActive(link.path) ? "active" : ""}`}
                  >
                    <span className="nav-icon">{link.icon}</span>
                    <span>{link.label}</span>
                  </Link>
                ))}

                {/* More dropdown */}
                <div className={`more-dropdown ${isMoreActive ? "more-active" : ""}`} ref={moreRef}>
                  <button
                    className={`nav-link more-btn ${isMoreActive ? "active" : ""}`}
                    onClick={() => setMoreOpen(o => !o)}
                  >
                    <span>More</span>
                    <span className="more-chevron">{moreOpen ? "▲" : "▾"}</span>
                  </button>
                  {moreOpen && (
                    <div className="more-menu">
                      {NAV_MORE.map(link => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className={`more-item ${isActive(link.path) ? "active" : ""}`}
                          onClick={() => setMoreOpen(false)}
                        >
                          <span className="more-item-icon">{link.icon}</span>
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <button className="scan-button" onClick={() => navigate("/scan")} title="Scan QR">
                  <span>📷</span>
                  <span className="scan-text">Scan</span>
                </button>

                <button className="logout-button" onClick={handleLogout}>
                  <span>↪</span>
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <div className="auth-buttons">
                <Link to="/login" className="nav-link">Sign In</Link>
                <Link to="/register" className="nav-link" style={{ background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', borderRadius: 10 }}>
                  Register Free
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile hamburger */}
          <div className="mobile-actions">
            <button className="menu-button" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      <div className={`mobile-nav ${menuOpen ? "open" : ""}`}>
        <div className="mobile-nav-content">
          {loginId ? (
            <>
              <div className="nav-section">
                <div className="nav-title">Menu</div>
                {ALL_NAV.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`mobile-nav-link ${isActive(link.path) ? "active" : ""}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="nav-icon">{link.icon}</span>
                    <span>{link.label}</span>
                  </Link>
                ))}
                <Link
                  to="/scan"
                  className={`mobile-nav-link ${isActive("/scan") ? "active" : ""}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="nav-icon">📷</span>
                  <span>Scan QR</span>
                </Link>
              </div>
              <div className="nav-footer">
                <button className="mobile-logout-button" onClick={handleLogout}>
                  <span>↪</span>
                  <span>Logout</span>
                </button>
              </div>
            </>
          ) : (
            <div className="mobile-auth-section">
              <Link to="/login" className="mobile-nav-link btn-login" onClick={() => setMenuOpen(false)}>Sign In</Link>
              <Link to="/register" className="mobile-nav-link btn-register" onClick={() => setMenuOpen(false)}>Register Gym Free</Link>
            </div>
          )}
        </div>
      </div>

      {menuOpen && <div className="overlay" onClick={() => setMenuOpen(false)} />}
    </>
  )
}

export default Header
